import { LegacyPortalDashboardService } from "@spwn-portal/grpc";
import firebase from "firebase/app";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import useSWRImmutable from "swr/immutable";

import { useConnectClient } from "../../utility/connectWeb";
import { isFeatureEnabled } from "../../utility/isFeatureEnabled";

export type ReaderCount = {
  readerId: string;
  count: number;
};

export type IssuedTicketCountsByReader = {
  ticketId: string;
  checkedInCountsByReader: ReaderCount[];
  redeemedCountsByReader: ReaderCount[];
};

type FetchResponse = {
  tickets: IssuedTicketCountsByReader[];
  updatedAt: Date;
};

export type FetchAdminTicketCountsByReaderError = {
  message: string;
};

const SWRKey = "fetchAdminTicketCountsByReader";

export const useFetchAdminTicketCountsByReader = () => {
  const { search } = useLocation();

  const { ticketIds, enabled } = useMemo(() => {
    const params = new URLSearchParams(search);
    return {
      ticketIds: params.getAll("ticket_id"),
      enabled: isFeatureEnabled({ featureName: "showReaderBreakdown" }),
    };
  }, [search]);

  const auth = firebase.auth();
  const connectClient = useConnectClient(LegacyPortalDashboardService);
  const currentUserId = auth.currentUser?.uid ?? null;

  const {
    data,
    isLoading,
    isValidating,
    error,
    mutate,
  } = useSWRImmutable<FetchResponse, FetchAdminTicketCountsByReaderError>(
    enabled && currentUserId && ticketIds.length > 0
      ? [SWRKey, currentUserId, ticketIds]
      : null,
    async () => {
      const response = await connectClient.getAllIssuedTicketCountsByReader({
        ticketIds,
      });
      const tickets: IssuedTicketCountsByReader[] = response.tickets.map(
        (t) => ({
          ticketId: t.ticketId,
          checkedInCountsByReader: t.checkedInCountsByReader.map((r) => ({
            readerId: r.readerId,
            count: r.count,
          })),
          redeemedCountsByReader: t.redeemedCountsByReader.map((r) => ({
            readerId: r.readerId,
            count: r.count,
          })),
        })
      );
      return {
        tickets,
        updatedAt: new Date(),
      };
    }
  );

  const countsByTicketId = useMemo(() => {
    const map = new Map<string, IssuedTicketCountsByReader>();
    for (const t of data?.tickets ?? []) {
      map.set(t.ticketId, t);
    }
    return map;
  }, [data]);

  return {
    enabled,
    countsByTicketId,
    isLoading,
    isValidating,
    error,
    lastUpdated: data?.updatedAt ?? null,
    mutate,
  };
};
